export interface WorkspaceShareInputs {
  /** Member can manage sharing (Admin/Owner) — mirrors the backend gate. */
  canManage: boolean;
  /** Library master setting `allow_soc_code`. */
  allowSocCode: boolean;
  /** Per-quiz `sharable` flag. */
  sharable: boolean;
}

export interface WorkspaceShareState {
  /** Toggle shown on + Link/Code visible. */
  isSharingEnabled: boolean;
  /** Toggle is non-interactive. */
  isControlsDisabled: boolean;
  /** Show the "Disabled by your library admin" note. */
  showDisabledNote: boolean;
}

/**
 * Derives the Share dialog state for a shared-library quiz from role + the
 * library setting + the quiz flag. Single source of truth for the scenario
 * matrix:
 *
 *   - Library `allow_soc_code` OFF → disabled toggle + admin note, for everyone.
 *   - Library ON: Admin/Owner toggle freely; Editors/Viewers are read-only
 *     (Link/Code when shared, otherwise the admin note).
 *
 * The library setting supersedes the per-quiz flag, so sharing only counts as
 * "on" when both are true.
 */
export function deriveWorkspaceShareState({
  canManage,
  allowSocCode,
  sharable,
}: WorkspaceShareInputs): WorkspaceShareState {
  const isSharingEnabled = allowSocCode && sharable;
  const canToggle = canManage && allowSocCode;

  return {
    isSharingEnabled,
    isControlsDisabled: !canToggle,
    // Off + the viewer can't turn it on → it's been disabled for them.
    showDisabledNote: !isSharingEnabled && !canToggle,
  };
}
